import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useEvent, useWindowSize } from 'react-use';

import { css, Global } from '@emotion/react';
import { Box, BoxProps, Divider, Flex, FlexProps, Loader } from '@opendoor/bricks/core';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';
import { MarginProps, PaddingProps } from '@opendoor/bricks/system/theme';
import { space } from '@opendoor/bricks/theme/eero';
import { BREAKPOINTS_DICT } from '@opendoor/bricks/theme/legacy/foundations/mediaBreakpoints';
import { ThemedLite } from '@opendoor/bricks/theme/ODThemeLite';
import { AddContextualData } from '@opendoor/observability';

import { useBrowserCompatibilityData } from 'shared/helpers/globals';
import { useMeasure } from 'shared/helpers/hooks/reactUseOverrides';

import { Footer } from 'platform/components/layout/Footer';
import { NovoFooter } from 'platform/components/layout/NovoFooter';
import {
  DesktopNovoPrimaryNavBar,
  MobileNovoPrimaryNavBar,
} from 'platform/components/layout/NovoPrimaryNavBar';
import {
  ActiveTabNameContext,
  MobilePrimaryNavBar,
  SELLING_TAB_NAME,
} from 'platform/components/layout/PrimaryNavBar';

export const LAYOUT_CONTENT_PADDING_XSMALL = 5;
export const LAYOUT_CONTENT_PADDING_SMALL = 7;
export const LAYOUT_CONTENT_PADDING_MEDIUM = 7;
export const LAYOUT_CONTENT_PADDING_LARGE = 8;
export const LAYOUT_CONTENT_PADDING = [
  LAYOUT_CONTENT_PADDING_XSMALL,
  LAYOUT_CONTENT_PADDING_SMALL,
  LAYOUT_CONTENT_PADDING_MEDIUM,
  LAYOUT_CONTENT_PADDING_LARGE,
];

/** Allows content to extend beyond the page's layout padding */
export const LAYOUT_MOBILE_FULL_BLEED_PROPS: FlexProps = {
  position: 'relative',
  left: -1 * LAYOUT_CONTENT_PADDING_SMALL,
  width: `calc(100% + ${space[LAYOUT_CONTENT_PADDING_SMALL] * 2}px)`,
  px: LAYOUT_CONTENT_PADDING_SMALL,
};

/**
 * Returns whether the Layout will be desktop or mobile.
 *
 * This is important for some components where the layout state must be in sync (e.g. More).
 */
export const useLayoutState: () => 'desktop' | 'mobile' | 'tablet' = () => {
  const { isDesktop, isTablet } = useDevice();
  if (isDesktop) return 'desktop';
  return isTablet ? 'tablet' : 'mobile';
};

export interface LayoutProps extends PropsWithChildren {
  /** Content which is fixed at the top of the Layout. */
  renderHeader?: () => React.ReactNode;
  /** By default, Layout will render a nav bar. Set to true to override this behavior. */
  hideNav?: boolean;
  /** By default, Layout will render a footer under the main content. Set to true to override this behavior. */
  hideFooter?: boolean;
  /** Content which is fixed at the bottom of the Layout. */
  stickyBottomContent?: React.ReactNode;
  /** If true, covers main content with a loading UI. */
  isLoading?: boolean;
  /** By default, Layout will check scroll position to show or hide divider. Set to true to override this behavior. */
  shouldShowDivider?: boolean;
  isNovoEnabled?: boolean;
  minHeight?: BoxProps['minHeight'];
  closingOrClosedOffer?: boolean;
}

/**
 * Defines the a Full Page layout.
 */
export function FullPageLayout({ children }: { children: React.ReactNode }) {
  return (
    <Box data-testid="layout-container">
      <Box as="main">{children}</Box>
    </Box>
  );
}
/**
 * Defines the overall Hub layout in all form factors.
 *
 * Mobile:
 * ---------------------
 * |    Header         |
 * |-------------------|
 * |    Main           |
 * |    Content        |
 * |                   |
 * |    Footer         |
 * |-------------------|
 * |StickyBottomContent|
 * |-------------------|
 * |    Global Nav     |
 * ---------------------
 *
 * Desktop:
 * -------------------------
 * | G |    Header         |
 * | l |-------------------|
 * | o |    Main           |
 * | b |    Content        |
 * | a |                   |
 * | l |                   |
 * |   |                   |
 * | N |    Footer         |
 * | a |-------------------|
 * | v |StickyBottomContent|
 * -------------------------
 */
export const Layout: React.FC<LayoutProps & MarginProps & PaddingProps> = (props) => {
  const state = useLayoutState();

  const desktopLayout = useDesktopLayout(props);
  const mobileLayout = useMobileLayout(props);
  const { isOpendoorAppEvoWebView } = useBrowserCompatibilityData();

  const layoutContent = (
    <>
      <AddContextualData data={{ deviceSize: state }}>
        {state === 'mobile' ? mobileLayout : desktopLayout}
      </AddContextualData>
      {/* 1x1 ad tracking pixels added as first-level children of the body
    element cause layout issues on pages which try to consume exactly
    100% of the vertical space (thus avoiding a vertical scrollbar).
    `display: none` allows us to hide them from the document flow while
    still triggering the requests. */}
      <Global
        styles={css`
          body > img {
            display: none;
          }
          ${isOpendoorAppEvoWebView
            ? `* {
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        -khtml-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
        -webkit-user-drag: none;
        -moz-user-drag: none;
        -o-user-drag: none;
        user-drag: none;
      }`
            : ''}
        `}
      />
    </>
  );
  return props.isNovoEnabled ? (
    <ThemedLite theme="novo">{layoutContent}</ThemedLite>
  ) : (
    layoutContent
  );
};

/**
 * ---------------------
 * |    Header         |
 * |-------------------|
 * |    Main           |
 * |    Content        |
 * |    Container      |
 * |                   |
 * |-------------------|
 * |StickyBottomContent|
 * |-------------------|
 * |    Global Nav     |
 * ---------------------
 *
 * useMobileLayout hook is used for rendering page content in the mobile layout.
 * We structure it in a React Hook instead of React Component to avoid re-rendering on window resizing.
 */
const useMobileLayout = ({
  hideNav,
  hideFooter,
  renderHeader,
  children,
  stickyBottomContent,
  isLoading,
  shouldShowDivider,
  minHeight,
  isNovoEnabled,
  closingOrClosedOffer,
  ...marginProps
}: LayoutProps & MarginProps) => {
  const [topRef, { height: topHeight }] = useMeasure();
  const [bottomRef, { height: bottomHeight }] = useMeasure();
  const activeTabName = useContext(ActiveTabNameContext);
  const { isOpendoorAppEvoWebView, isOpendoorAppWebView } = useBrowserCompatibilityData();

  const [showDivider, setShowDivider] = useState(shouldShowDivider || false);
  // show bottom border when content is scrolled
  useEvent(
    'scroll',
    useCallback(() => setShowDivider(window.scrollY > 0), []),
  );

  const mobilePrimaryNavBar =
    activeTabName === SELLING_TAB_NAME ? <MobileNovoPrimaryNavBar /> : <MobilePrimaryNavBar />;
  return (
    // Use position="fixed" to implement sticky top/bottom content.
    // Can't use height 100% and flex because that disables mobile URL bar resizing.
    // https://developers.google.com/web/updates/2016/12/url-bar-resizing
    <>
      <Box
        ref={topRef}
        top={0}
        height="auto"
        width="100%"
        bgColor="neutrals0"
        // Use zIndex to make sure header is visible on top of main content
        // Mapbox uses a z-index of 2, and some elements overlaying the maps use z-index of 3.
        // Thus setting it to 5 to leave 1 more space
        zIndex={5}
      >
        {renderHeader && renderHeader()}
        {(showDivider || shouldShowDivider) && <Divider data-testid="layout-divider" />}
      </Box>
      <MainContentContainer
        leftInset={0}
        topInset={topHeight}
        bottomInset={bottomHeight}
        hideFooter={hideFooter || isOpendoorAppEvoWebView}
        isLoading={isLoading}
        minHeight={minHeight}
        isNovoEnabled={isNovoEnabled}
        closingOrClosedOffer={closingOrClosedOffer}
        {...marginProps}
      >
        {children}
      </MainContentContainer>
      <Box
        ref={bottomRef}
        position="fixed"
        bottom={0}
        height="auto"
        width="100%"
        bgColor="transparent"
        // Matches the zIndex required in desktop
        zIndex={6}
      >
        {stickyBottomContent}
        {!hideNav && !isOpendoorAppWebView && !isOpendoorAppEvoWebView && mobilePrimaryNavBar}
      </Box>
    </>
  );
};

/**
 * -------------------------
 * | G |    Header         |
 * | l |-------------------|
 * | o |    Main           |
 * | b |    Content        |
 * | a |    Container      |
 * | l |                   |
 * |   |                   |
 * | N |                   |
 * | a |-------------------|
 * | v |StickyBottomContent|
 * -------------------------
 *
 * useDesktopLayout hook is used for rendering page content in the desktop layout.
 * We structure it in a React Hook instead of React Component to avoid re-rendering on window resizing.
 */
const useDesktopLayout = ({
  hideNav,
  hideFooter,
  renderHeader,
  children,
  stickyBottomContent,
  isLoading,
  minHeight,
  isNovoEnabled,
  closingOrClosedOffer = false,
  shouldShowDivider = true,
  ...marginProps
}: LayoutProps & MarginProps) => {
  const [leftRef, { width: leftWidth }] = useMeasure();
  const [topRef, { height: topHeight }] = useMeasure();
  const [bottomRef, { height: bottomHeight }] = useMeasure();

  const desktopPrimaryNavBar = <DesktopNovoPrimaryNavBar />;

  return (
    <>
      <Box
        ref={topRef}
        top={0}
        height="auto"
        width="100%"
        pl={`${leftWidth}px`}
        bgColor="neutrals0"
        // Use zIndex to make sure header is visible on top of main content
        // Mapbox uses a z-index of 2, and some elements overlaying the maps use z-index of 3.
        // Thus setting it to 5 to leave 1 more space
        zIndex={5}
      >
        {renderHeader && renderHeader()}
        {shouldShowDivider && <Divider data-testid="layout-divider" />}
      </Box>
      <MainContentContainer
        leftInset={leftWidth}
        topInset={topHeight}
        bottomInset={bottomHeight}
        hideFooter={hideFooter}
        isLoading={isLoading}
        minHeight={minHeight}
        isNovoEnabled={isNovoEnabled}
        closingOrClosedOffer={closingOrClosedOffer}
        {...marginProps}
      >
        {children}
      </MainContentContainer>
      <Box
        ref={bottomRef}
        position="fixed"
        bottom={0}
        height="auto"
        width="100%"
        pl={`${leftWidth}px`}
        bgColor="transparent"
      >
        {stickyBottomContent}
      </Box>
      <Box
        ref={leftRef}
        position="fixed"
        top={0}
        left={0}
        height="100%"
        width="auto"
        bgColor="neutrals0"
        // Use zIndex to make sure left nav is visible on top of header, which has a zIndex of 5
        zIndex={6}
      >
        {!hideNav && desktopPrimaryNavBar}
      </Box>
    </>
  );
};

/**
 * Renders the container of the main content, for all layouts.
 *
 * To make MainContentContainer generic, the parent layout passes in insets corresponding to its fixed elements.
 * Then, MainContentContainer renders padding over those insets to ensure all content can be viewed when scrolled.
 *
 * isLoading = false | undefined
 * -------------------------
 * | L |    Top Inset      |
 * | e |-------------------|
 * | f |    Main           |
 * | t |    Content        |
 * |   |                   |
 * | I |                   |
 * | n |                   |
 * | s |    Footer         |
 * | e |-------------------|
 * | t |    Bottom Inset   |
 * -------------------------
 *
 * isLoading = true
 * -------------------------
 * | L |    Top Inset      |
 * | e |-------------------|
 * | f |                   |
 * | t |                   |
 * |   |    Loading        |
 * | I |                   |
 * | n |                   |
 * | s |                   |
 * | e |-------------------|
 * | t |    Bottom Inset   |
 * -------------------------
 */
export const MainContentContainer = ({
  leftInset,
  topInset,
  bottomInset,
  hideFooter,
  isLoading,
  isNovoEnabled,
  children,
  minHeight,
  closingOrClosedOffer,
  ...marginProps
}: {
  leftInset: number;
  topInset: number;
  bottomInset: number;
  hideFooter: boolean | undefined;
  isLoading: boolean | undefined;
  isNovoEnabled?: boolean;
  minHeight?: BoxProps['minHeight'];
  closingOrClosedOffer?: boolean;
} & MarginProps &
  PropsWithChildren) => {
  const { width: windowWidth, height: windowHeight } = useWindowSize();
  const [footerRef, { height: footerHeight }] = useMeasure();

  const loaderVisibilityState = useLoaderVisibilityState(isLoading);
  const mainContentSize = useMainContentSize(leftInset, topInset, bottomInset, footerHeight);

  const footer = isNovoEnabled ? (
    <NovoFooter closingOrClosedOffer={closingOrClosedOffer} />
  ) : (
    <Footer />
  );

  return (
    <Box
      data-testid="layout-container"
      bgColor="neutrals0"
      // Set padding to ensure all content can be viewed when scrolled.
      pl={`${leftInset}px`}
      pt={isNovoEnabled ? 0 : `${topInset}px`}
      pb={`${bottomInset + footerHeight}px`}
      // position/minHeight required to show the footer at the bottom of the content or page, whichever is lower.
      // https://www.freecodecamp.org/news/how-to-keep-your-footer-where-it-belongs-59c6aa05c59c/
      position="relative"
      minHeight={minHeight || windowHeight}
      overflowX="hidden"
      {...marginProps}
    >
      {/* Loader */}
      {loaderVisibilityState !== 'invisible' && (
        <Flex
          alignItems="center"
          data-testid="loader-container"
          width={`${windowWidth - leftInset}px`}
          height={`${windowHeight - topInset - bottomInset}px`}
          opacity={loaderVisibilityState === 'visible' ? 1 : 0}
          transition={
            loaderVisibilityState === 'transitioning'
              ? `opacity ${LOADER_FADE_OUT_MS}ms linear`
              : 'none'
          }
        >
          <Loader data-testid="section-loader" />
        </Flex>
      )}

      {/* Main content */}
      <LoaderVisibilityContext.Provider value={loaderVisibilityState}>
        <MainContentSizeContext.Provider value={mainContentSize}>
          <Box
            as="main"
            // Hide the main content instead of not rendering at all.
            // This is necessary so that child components can do their loading logic.
            display={loaderVisibilityState !== 'invisible' ? 'none' : undefined}
            minHeight={windowHeight - topInset - bottomInset - footerHeight}
          >
            {children}
          </Box>
        </MainContentSizeContext.Provider>
      </LoaderVisibilityContext.Provider>

      {/* Footer */}
      {!hideFooter && (
        <Box
          as="footer"
          ref={footerRef}
          // Hide the footer too.
          display={loaderVisibilityState !== 'invisible' ? 'none' : undefined}
          // position/bottom required to show the footer at the bottom of the content or page, whichever is lower.
          // https://www.freecodecamp.org/news/how-to-keep-your-footer-where-it-belongs-59c6aa05c59c/
          position="absolute"
          bottom={bottomInset}
          height="auto"
          width={`${windowWidth - leftInset}px`}
        >
          {footer}
        </Box>
      )}
    </Box>
  );
};

/**
 * Renders the content of the main content, for all layouts.
 */
export const LayoutContent: React.FC<FlexProps> = ({ children, ...props }) => (
  <Flex
    data-testid="layout-content"
    flexDirection="column"
    alignItems={['inherit', null, 'center']}
    mx="auto"
    width="100%"
    maxWidth={BREAKPOINTS_DICT.MD}
    p={LAYOUT_CONTENT_PADDING}
    {...props}
  >
    {children}
  </Flex>
);

// Duration of the fade-out animation
const LOADER_FADE_OUT_MS = 300;

type VisibilityState = 'visible' | 'transitioning' | 'invisible';
export const LoaderVisibilityContext = createContext<VisibilityState>('invisible');
const useLoaderVisibilityState = (isLoading: boolean | undefined): VisibilityState => {
  const [state, setState] = React.useState<VisibilityState>(isLoading ? 'visible' : 'invisible');

  useEffect(() => {
    if (isLoading) {
      setState('visible');
    } else {
      setState((previousState) => {
        return previousState === 'visible' ? 'transitioning' : previousState;
      });
    }
  }, [isLoading]);

  useEffect(() => {
    if (state === 'transitioning') {
      const visibilityTimeout = setTimeout(() => setState('invisible'), LOADER_FADE_OUT_MS);
      return () => clearTimeout(visibilityTimeout);
    }
  }, [state]);

  return state;
};

type MainContentSize = { height: number; width: number };
export const MainContentSizeContext = createContext<MainContentSize>({
  height: 0,
  width: 0,
});
const useMainContentSize = (
  leftInset: number,
  topInset: number,
  bottomInset: number,
  footerHeight: number,
): MainContentSize => {
  const { width: windowWidth, height: windowHeight } = useWindowSize();
  const [contentSize, setContentSize] = useState<MainContentSize>({
    height: windowHeight,
    width: windowWidth,
  });

  useEffect(
    () =>
      setContentSize({
        width: windowWidth - leftInset,
        height: windowHeight - topInset - bottomInset - footerHeight,
      }),
    [windowWidth, windowHeight, leftInset, topInset, bottomInset, footerHeight],
  );

  return contentSize;
};
